import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
/** Spinner-only skeleton; the accessible label describes capture, upload or image download. */
export function ImageLoading({ label = 'Loading image…', className = '', ...props }) {
    return _jsx("span", { ...props, className: `oq-image-loading ${className}`, role: "status", "aria-label": label, children: _jsxs("svg", { className: "oq-image-loading__spinner", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: [_jsx("circle", { className: "oq-image-loading__track", cx: "12", cy: "12", r: "9", stroke: "currentColor", strokeWidth: "2" }), _jsx("path", { d: "M12 3a9 9 0 0 1 9 9", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" })] }) });
}
function ImageContent({ pending, failed, fill, compact, frameClassName = '', frameStyle, loadingLabel = 'Loading image…', errorLabel = 'Image unavailable', onLoad, onError, ...props }) {
    const image = useRef(null);
    const [state, setState] = useState('loading');
    useEffect(() => {
        const element = image.current;
        if (!element || pending)
            return;
        let active = true;
        const ready = async () => {
            if (!element.naturalWidth)
                return;
            // Wait for pixels, not just the URL or HTTP response. Cached images may already be complete on mount.
            try {
                await element.decode?.();
            }
            catch { /* A loaded image may not support decode (for example SVG). */ }
            if (active)
                setState('ready');
        };
        const failed = () => { if (active)
            setState('error'); };
        element.addEventListener('load', ready);
        element.addEventListener('error', failed);
        if (element.complete) {
            if (element.naturalWidth)
                void ready();
            else if (element.getAttribute('src') || element.getAttribute('srcset'))
                failed();
        }
        return () => { active = false; element.removeEventListener('load', ready); element.removeEventListener('error', failed); };
    }, [props.src, pending]);
    const currentState = failed || (!pending && !props.src && !props.srcSet) ? 'error' : state;
    const loading = currentState !== 'error' && (pending || state === 'loading');
    return _jsxs("span", { className: `oq-image-frame${fill ? ' oq-image-frame--fill' : ''}${compact ? ' oq-image-frame--compact' : ''} ${frameClassName}`, style: frameStyle, "data-image-state": loading ? 'loading' : currentState, "aria-busy": loading, children: [(props.src || props.srcSet) && _jsx("img", { ...props, ref: image, onLoad: onLoad, onError: onError }), loading && _jsx(ImageLoading, { label: loadingLabel }), !loading && currentState === 'error' && _jsxs("span", { className: "oq-image-loading oq-image-loading--error", role: "status", "aria-label": errorLabel, title: errorLabel, children: [_jsx("span", { className: "oq-image-loading__error-label", "aria-hidden": "true", children: errorLabel }), _jsx("span", { className: "oq-image-loading__error-mark", "aria-hidden": "true", children: "!" })] })] });
}
/** Source identity resets loading/errors synchronously; a late load from the old image cannot clear the new one. */
export function ImageWithLoader(props) {
    return _jsx(ImageContent, { ...props }, `${props.src ?? ''}\n${props.srcSet ?? ''}`);
}
//# sourceMappingURL=ImageLoading.js.map